/**
 * Sons do painel: aviso de mensagem nova e toque de chamada.
 *
 * Tudo é sintetizado com osciladores — nenhum arquivo de áudio para hospedar,
 * carregar ou versionar.
 *
 * O detalhe que faz a diferença entre funcionar e não funcionar: navegadores
 * criam o AudioContext no estado `suspended` até haver um gesto do usuário. Um
 * som disparado antes disso não toca e não avisa nada. Por isso o contexto é
 * único, compartilhado, e retomado no primeiro clique ou tecla — e a partir daí
 * qualquer aviso funciona, inclusive com a aba em segundo plano.
 */

let context: AudioContext | null = null;
let unlocked = false;
let ringTimer: number | null = null;

function getContext(): AudioContext | null {
  if (context) return context;

  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!Ctor) return null;

  context = new Ctor();
  return context;
}

/**
 * Liga o áudio ao primeiro gesto do usuário. Chamar mais de uma vez é
 * inofensivo; os listeners se removem sozinhos.
 */
export function installAudioUnlock(): void {
  if (unlocked) return;

  const unlock = () => {
    unlocked = true;
    const ctx = getContext();
    if (ctx?.state === 'suspended') void ctx.resume();
    for (const event of ['pointerdown', 'keydown'] as const) {
      window.removeEventListener(event, unlock);
    }
  };

  for (const event of ['pointerdown', 'keydown'] as const) {
    window.addEventListener(event, unlock, { once: true, passive: true });
  }
}

export function isAudioUnlocked(): boolean {
  return unlocked && getContext()?.state === 'running';
}

/** Um bipe com envelope suave — sem o clique seco de ligar/desligar no zero. */
function beep(frequency: number, startOffset: number, duration: number, volume: number): void {
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') void ctx.resume();

  const start = ctx.currentTime + startOffset;
  const oscillator = ctx.createOscillator();
  const gain = ctx.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;

  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(volume, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  oscillator.connect(gain).connect(ctx.destination);
  oscillator.start(start);
  oscillator.stop(start + duration + 0.02);
}

/** Aviso curto de mensagem nova: duas notas ascendentes, discretas. */
export function playMessageChime(): void {
  beep(660, 0, 0.12, 0.14);
  beep(880, 0.12, 0.16, 0.12);
}

/** Toque de chamada, repetido até `stopRingtone`. */
export function startRingtone(): void {
  stopRingtone();

  const ring = () => {
    beep(440, 0, 0.35, 0.18);
    beep(480, 0.4, 0.35, 0.18);
  };

  ring();
  ringTimer = window.setInterval(ring, 3000);
}

export function stopRingtone(): void {
  if (ringTimer !== null) {
    window.clearInterval(ringTimer);
    ringTimer = null;
  }
}
